import { useEffect, useRef } from "react";

const MAPS_SCRIPT_RE = /maps\.googleapis\.com\/maps\/api\/js/;

const isGoogleMapsReady = () =>
    typeof window !== "undefined" &&
    window.google?.maps?.places;

const findExistingMapsScript = () =>
    Array.from(document.getElementsByTagName("script")).find((s) => s.src && MAPS_SCRIPT_RE.test(s.src));

const waitForGoogleMaps = (callback) => {
    if (isGoogleMapsReady()) {
        callback();
        return () => {};
    }

    let cancelled = false;
    const interval = setInterval(() => {
        if (cancelled) return;
        if (isGoogleMapsReady()) {
            clearInterval(interval);
            clearTimeout(timeout);
            callback();
        }
    }, 50);

    const timeout = setTimeout(() => clearInterval(interval), 15000);

    return () => {
        cancelled = true;
        clearInterval(interval);
        clearTimeout(timeout);
    };
};

/**
 * Injects the Google Maps JS API once and notifies when Places is ready.
 * Reuses an existing script tag if another component (or a prior mount) already added it.
 * Polls until Places is available so missed load events (e.g. Strict Mode) do not leave consumers stuck.
 */
const LoadGoogleMapsAPI = ({ onLoad }) => {
    const onLoadRef = useRef(onLoad);
    onLoadRef.current = onLoad;

    useEffect(() => {
        const notify = () => onLoadRef.current?.();

        if (isGoogleMapsReady()) {
            notify();
            return;
        }

        if (!findExistingMapsScript()) {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
            script.async = true;
            script.defer = true;
            document.head.appendChild(script);
        }

        return waitForGoogleMaps(notify);
    }, []);

    return null;
};

export default LoadGoogleMapsAPI;
